import { useState } from "react";
import { useCustom, useCustomMutation, useGetLocale, useList, useTranslate } from "@refinedev/core";
import type { HttpError } from "@refinedev/core";
import {
  Button,
  DataTable,
  Field,
  Modal,
  PasswordField,
  SelectField,
  StatusBadge,
  type Column,
} from "@fawkes/ui";

import { ErrorState, LoadingState } from "../../components/states";
import type { Locale } from "../../i18n/messages";
import { formatDateTime } from "../portal/format";
import type { AdminOrganization, BoundCredential, BulkOutcome, OrganizationHistoryRow } from "./types";
import { PairingDialog, PairingPanel } from "./OrganizationPairing";

// jmix-9.3 + parity: Organizations — list (multi-select bulk soft-delete / token-cache eviction, each behind
// a confirm dialog with live counts) + create (helper texts on all fields) + detail (editable identity →
// PUT, single soft-delete/restore behind confirm dialogs, audit history). The create/edit clientSecret is
// a password input; it is reversibly encrypted server-side (OrganizationSecretEncryptor, L2) and NEVER echoed
// back. 401/403 → logout via authProvider; 409 (duplicate clientId) / 400 (validation) surfaced inline.

function statusOf(error: unknown): number | undefined {
  return (error as HttpError | undefined)?.statusCode;
}

type OrganizationStatusFilter = "active" | "removed" | "all";

export function Organizations() {
  const t = useTranslate();
  const [status, setStatus] = useState<OrganizationStatusFilter>("active");
  const { data, isLoading, isError, refetch } = useList<AdminOrganization>({
    resource: "admin/organizations",
    pagination: { mode: "off" },
    // jmix-6 (restore) + parity: ?status=removed lists the soft-deleted organizations (reachable for restore);
    // ?status=all lists both. Default (active) sends no filter → the live list. The dataProvider maps an
    // eq filter to ?status=<value>.
    filters: status === "active" ? [] : [{ field: "status", operator: "eq", value: status }],
  });
  const { mutate, isLoading: mutating } = useCustomMutation();

  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [createOpen, setCreateOpen] = useState(false);
  const [form, setForm] = useState({ displayName: "", baseUrl: "", clientId: "", clientSecret: "" });
  const [createError, setCreateError] = useState<string | undefined>();
  const [bulkBanner, setBulkBanner] = useState<string | undefined>();
  const [detail, setDetail] = useState<AdminOrganization | null>(null);
  // The pending bulk action awaiting an explicit confirm; null when no confirm dialog is open.
  const [bulkConfirm, setBulkConfirm] = useState<"bulk-delete" | "bulk-evict-token-cache" | null>(null);

  if (isLoading) {
    return <LoadingState />;
  }
  if (isError) {
    return <ErrorState onRetry={() => void refetch()} />;
  }

  const rows = data?.data ?? [];
  // Bulk soft-delete only acts on ACTIVE rows; in the All view, gate the select-all/select to live rows.
  const selectableRows = rows.filter((r) => r.active);
  const allSelected = selectableRows.length > 0 && selectableRows.every((r) => selectedIds.has(r.id));
  const selectedCount = selectedIds.size;

  function toggle(id: string) {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  }

  function toggleAll() {
    setSelectedIds(allSelected ? new Set() : new Set(selectableRows.map((r) => r.id)));
  }

  function openCreate() {
    setForm({ displayName: "", baseUrl: "", clientId: "", clientSecret: "" });
    setCreateError(undefined);
    setCreateOpen(true);
  }

  function submitCreate() {
    setCreateError(undefined);
    if (!form.baseUrl.trim() || !form.clientId.trim() || !form.clientSecret) {
      setCreateError(t("admin.organizations.create.error.required"));
      return;
    }
    mutate(
      {
        url: "admin/organizations",
        method: "post",
        values: {
          displayName: form.displayName.trim() || null,
          baseUrl: form.baseUrl.trim(),
          clientId: form.clientId.trim(),
          clientSecret: form.clientSecret,
        },
      },
      {
        onSuccess: () => {
          setCreateOpen(false);
          // Don't leave the outbound client secret sitting in component state after it's persisted —
          // it's already encrypted server-side (L2) and never re-fetched, so drop the plaintext now.
          setForm({ displayName: "", baseUrl: "", clientId: "", clientSecret: "" });
          void refetch();
        },
        onError: (error) => setCreateError(createErrorMessage(error, t)),
      },
    );
  }

  function runBulk(action: "bulk-delete" | "bulk-evict-token-cache") {
    setBulkConfirm(null);
    setBulkBanner(undefined);
    const ids = [...selectedIds];
    if (ids.length === 0) {
      return;
    }
    mutate(
      { url: `admin/organizations/${action}`, method: "post", values: { ids } },
      {
        onSuccess: (res) => {
          const outcome = res?.data as BulkOutcome | undefined;
          setSelectedIds(new Set());
          void refetch();
          const lead = action === "bulk-delete" ? t("admin.organizations.bulk.deleted") : t("admin.organizations.bulk.evicted");
          // Surface succeeded + skipped(already-removed) with explanatory text (BulkOutcomeDto).
          const succeeded = outcome?.succeeded ?? 0;
          const skipped = outcome?.skipped ?? 0;
          let banner = `${lead}: ${succeeded} ${t("admin.organizations.bulk.result.succeeded")}`;
          if (skipped > 0) {
            banner += ` · ${skipped} ${t("admin.organizations.bulk.result.skipped")}`;
          }
          setBulkBanner(banner);
        },
        onError: () => setBulkBanner(t("admin.organizations.bulk.error")),
      },
    );
  }

  const columns: Column<AdminOrganization>[] = [
    {
      header: "",
      cell: (s) =>
        // Only live rows are bulk-selectable; a removed row has no checkbox (its bulk actions are no-ops).
        s.active ? (
          <input
            type="checkbox"
            aria-label={t("admin.organizations.selectRow")}
            checked={selectedIds.has(s.id)}
            onChange={() => toggle(s.id)}
          />
        ) : null,
    },
    { header: t("admin.organizations.column.displayName"), cell: (s) => s.displayName || "—" },
    { header: t("admin.organizations.column.clientId"), cell: (s) => s.clientId },
    { header: t("admin.organizations.column.baseUrl"), cell: (s) => s.baseUrl },
    {
      header: t("admin.organizations.column.status"),
      cell: (s) => (
        <StatusBadge tone={s.active ? "success" : "warning"}>
          {t(s.active ? "admin.common.active" : "admin.organizations.status.softDeleted")}
        </StatusBadge>
      ),
    },
    {
      header: t("admin.common.actions"),
      align: "right",
      cell: (s) => (
        <Button variant="secondary" onClick={() => setDetail(s)}>
          {t("admin.common.view")}
        </Button>
      ),
    },
  ];

  return (
    <div className="fawkes-admin-page">
      <div className="fawkes-admin__toolbar">
        <div className="fawkes-admin__bulk">
          <SelectField
            label={t("admin.organizations.filter.status")}
            value={status}
            onChange={(e) => {
              setSelectedIds(new Set());
              setStatus(e.target.value as OrganizationStatusFilter);
            }}
            options={[
              { value: "active", label: t("admin.organizations.filter.active") },
              { value: "removed", label: t("admin.organizations.filter.removed") },
              { value: "all", label: t("admin.organizations.filter.all") },
            ]}
          />
          {/* Select-all + bulk actions act only on live rows; the removed-only view has none to delete. */}
          {status !== "removed" ? (
            <label className="fawkes-admin__selectall">
              <input type="checkbox" checked={allSelected} onChange={toggleAll} aria-label={t("admin.organizations.selectAll")} />
              {t("admin.organizations.selectAll")}
            </label>
          ) : null}
          {status !== "removed" ? (
            <Button
              variant="secondary"
              disabled={mutating || selectedCount === 0}
              onClick={() => setBulkConfirm("bulk-delete")}
            >
              {`${t("admin.organizations.bulk.delete")} (${selectedCount})`}
            </Button>
          ) : null}
          <Button
            variant="secondary"
            disabled={mutating || selectedCount === 0}
            onClick={() => setBulkConfirm("bulk-evict-token-cache")}
          >
            {`${t("admin.organizations.bulk.evict")} (${selectedCount})`}
          </Button>
        </div>
        <Button onClick={openCreate}>{t("admin.organizations.create.open")}</Button>
      </div>

      {/* Epic 10: pair a backoffice with a one-time code instead of copying credentials by hand. */}
      <PairingPanel />

      {bulkBanner ? (
        <p className="fawkes-portal__banner fawkes-portal__banner--info" role="status">
          {bulkBanner}
        </p>
      ) : null}

      <DataTable
        columns={columns}
        rows={rows}
        getRowKey={(s) => s.id}
        empty={<span className="fawkes-portal__muted">{t("admin.organizations.empty")}</span>}
      />

      {/* Bulk soft-delete confirm (DANGER) */}
      <Modal
        open={bulkConfirm === "bulk-delete"}
        title={t("admin.organizations.bulk.confirm.deleteTitle")}
        onClose={() => setBulkConfirm(null)}
        footer={
          <>
            <Button variant="secondary" onClick={() => setBulkConfirm(null)}>
              {t("admin.common.cancel")}
            </Button>
            <Button variant="secondary" disabled={mutating} onClick={() => runBulk("bulk-delete")}>
              {t("admin.organizations.bulk.confirm.deleteYes")}
            </Button>
          </>
        }
      >
        <p>{`${selectedCount} ${t("admin.organizations.bulk.confirm.deleteCount")}`}</p>
        <p className="fawkes-portal__muted">{t("admin.organizations.bulk.confirm.deleteBody")}</p>
      </Modal>

      {/* Bulk evict-token-cache confirm */}
      <Modal
        open={bulkConfirm === "bulk-evict-token-cache"}
        title={t("admin.organizations.bulk.confirm.evictTitle")}
        onClose={() => setBulkConfirm(null)}
        footer={
          <>
            <Button variant="secondary" onClick={() => setBulkConfirm(null)}>
              {t("admin.common.cancel")}
            </Button>
            <Button disabled={mutating} onClick={() => runBulk("bulk-evict-token-cache")}>
              {t("admin.organizations.bulk.confirm.evictYes")}
            </Button>
          </>
        }
      >
        <p>{`${t("admin.organizations.bulk.confirm.evictCount")} ${selectedCount} ${t("admin.organizations.bulk.confirm.evictCountSuffix")}`}</p>
        <p className="fawkes-portal__muted">{t("admin.organizations.bulk.confirm.evictBody")}</p>
      </Modal>

      {/* Create */}
      <Modal
        open={createOpen}
        title={t("admin.organizations.create.title")}
        onClose={() => setCreateOpen(false)}
        footer={
          <>
            <Button variant="secondary" onClick={() => setCreateOpen(false)}>
              {t("admin.common.cancel")}
            </Button>
            <Button onClick={submitCreate} disabled={mutating}>
              {mutating ? t("admin.common.saving") : t("admin.common.save")}
            </Button>
          </>
        }
      >
        <Field
          label={t("admin.organizations.column.displayName")}
          value={form.displayName}
          onChange={(e) => setForm((f) => ({ ...f, displayName: e.target.value }))}
        />
        <p className="fawkes-portal__muted">{t("admin.organizations.help.displayName")}</p>
        <Field
          label={t("admin.organizations.column.baseUrl")}
          value={form.baseUrl}
          onChange={(e) => setForm((f) => ({ ...f, baseUrl: e.target.value }))}
        />
        <p className="fawkes-portal__muted">{t("admin.organizations.help.baseUrl")}</p>
        <Field
          label={t("admin.organizations.column.clientId")}
          value={form.clientId}
          onChange={(e) => setForm((f) => ({ ...f, clientId: e.target.value }))}
        />
        <p className="fawkes-portal__muted">{t("admin.organizations.help.clientId")}</p>
        <PasswordField
          label={t("admin.organizations.create.clientSecret")}
          revealLabel={t("auth.field.revealPassword")}
          hideLabel={t("auth.field.hidePassword")}
          value={form.clientSecret}
          onChange={(e) => setForm((f) => ({ ...f, clientSecret: e.target.value }))}
        />
        <p className="fawkes-portal__muted">{t("admin.organizations.help.clientSecret")}</p>
        {createError ? (
          <p className="fawkes-portal__error" role="alert">
            {createError}
          </p>
        ) : null}
      </Modal>

      <OrganizationDetailModal
        key={detail?.id ?? "none"}
        organization={detail}
        onClose={() => setDetail(null)}
        onChanged={() => {
          setDetail(null);
          setSelectedIds(new Set());
          void refetch();
        }}
      />
    </div>
  );
}

// Map a create failure to an actionable message: a 409 is a duplicate clientId; a server-side encryptor-
// unconfigured failure (the dev/prod key/salt unset) surfaces a "configure the keys" hint when the API
// echoes that reason, else the generic message.
function createErrorMessage(error: unknown, t: (k: string) => string): string {
  const code = statusOf(error);
  if (code === 409) {
    return t("admin.organizations.create.error.duplicate");
  }
  const reason = (error as HttpError | undefined)?.message ?? "";
  if (/organization-secret|encryption key|encryptor/i.test(reason)) {
    return t("admin.organizations.create.error.noKeys");
  }
  return t("admin.organizations.create.error.generic");
}

// jmix-9.3 + parity: the organization detail — editable identity (PUT) + status + the single soft-delete/restore
// actions (behind confirm dialogs) + the audit-history panel (GET /{id}/history, owned by 5-3). A child
// component so the history useCustom is keyed to the open organization and not run for the whole list.
function OrganizationDetailModal({
  organization,
  onClose,
  onChanged,
}: {
  organization: AdminOrganization | null;
  onClose: () => void;
  onChanged: () => void;
}) {
  const t = useTranslate();
  const locale = (useGetLocale()() ?? "nl") as Locale;
  const { data, isLoading, isError, refetch } = useCustom<OrganizationHistoryRow[]>({
    url: `admin/organizations/${organization?.id}/history`,
    method: "get",
    queryOptions: { enabled: organization !== null },
  });
  // Epic 9: the M2M credentials bound to this organization (the only ones that may act for it once
  // binding is enforced). Keyed to the open organization like the history panel.
  const { data: boundData, isError: boundError } = useCustom<BoundCredential[]>({
    url: `admin/organizations/${organization?.id}/credentials`,
    method: "get",
    queryOptions: { enabled: organization !== null && organization.active },
  });
  const { mutate, isLoading: mutating } = useCustomMutation();
  const [actionError, setActionError] = useState<string | undefined>();
  const [actionNote, setActionNote] = useState<string | undefined>();
  const [repairOpen, setRepairOpen] = useState(false);
  // The pending single soft-delete / restore awaiting an explicit confirm.
  const [confirmAction, setConfirmAction] = useState<"delete" | "restore" | null>(null);

  function runSoftDeleteOrRestore() {
    if (!organization) {
      return;
    }
    setConfirmAction(null);
    setActionError(undefined);
    setActionNote(undefined);
    const request = organization.active
      ? ({ url: `admin/organizations/${organization.id}`, method: "delete" } as const)
      : ({ url: `admin/organizations/${organization.id}/restore`, method: "post" } as const);
    mutate(
      { ...request, values: {} },
      {
        onSuccess: () => onChanged(),
        onError: () => setActionError(t("admin.organizations.detail.action.error")),
      },
    );
  }

  // Edit identity (displayName/baseUrl/clientId) → PUT. Only offered for ACTIVE organizations.
  const { mutate: saveEdit, isLoading: savingEdit } = useCustomMutation();
  const [editOpen, setEditOpen] = useState(false);
  const [editForm, setEditForm] = useState({ displayName: "", baseUrl: "", clientId: "" });
  const [editError, setEditError] = useState<string | undefined>();

  function openEdit() {
    if (!organization) {
      return;
    }
    setEditForm({
      displayName: organization.displayName ?? "",
      baseUrl: organization.baseUrl,
      clientId: organization.clientId,
    });
    setEditError(undefined);
    setEditOpen(true);
  }

  function submitEdit() {
    if (!organization) {
      return;
    }
    setEditError(undefined);
    if (!editForm.baseUrl.trim() || !editForm.clientId.trim()) {
      setEditError(t("admin.organizations.detail.edit.error.required"));
      return;
    }
    saveEdit(
      {
        url: `admin/organizations/${organization.id}`,
        method: "put",
        values: {
          displayName: editForm.displayName.trim() || null,
          baseUrl: editForm.baseUrl.trim(),
          clientId: editForm.clientId.trim(),
        },
      },
      {
        onSuccess: () => {
          setEditOpen(false);
          setActionNote(t("admin.organizations.detail.edited"));
          // The identity edit is an audited change AND changes the detail row — refresh both.
          void refetch();
          onChanged();
        },
        onError: (error) =>
          setEditError(
            statusOf(error) === 409
              ? t("admin.organizations.detail.edit.error.duplicate")
              : t("admin.organizations.detail.edit.error.generic"),
          ),
      },
    );
  }

  // jmix-9.3 (re-home): the legacy "Test connection" action — mint an OAuth token for this organization and
  // probe its backoffice; a 404 on a random klant_uuid proves OAuth + routing (reported green by the API).
  const { mutate: testConnection, isLoading: testing } = useCustomMutation();
  const [conn, setConn] = useState<{ ok: boolean; reason: string } | "error" | undefined>();

  function runTestConnection() {
    if (!organization) {
      return;
    }
    setConn(undefined);
    testConnection(
      { url: `admin/organizations/${organization.id}/test-connection`, method: "post", values: {} },
      {
        onSuccess: (res) => setConn(res?.data as { ok: boolean; reason: string }),
        onError: () => setConn("error"),
      },
    );
  }

  const { mutate: rotateSecret, isLoading: rotating } = useCustomMutation();
  const [rotateOpen, setRotateOpen] = useState(false);
  const [newSecret, setNewSecret] = useState("");
  const [rotateError, setRotateError] = useState<string | undefined>();
  const [rotateDone, setRotateDone] = useState(false);

  function openRotate() {
    setNewSecret("");
    setRotateError(undefined);
    setRotateDone(false);
    setRotateOpen(true);
  }

  function submitRotate() {
    if (!organization) {
      return;
    }
    setRotateError(undefined);
    if (!newSecret) {
      setRotateError(t("admin.organizations.rotate.error.required"));
      return;
    }
    rotateSecret(
      {
        url: `admin/organizations/${organization.id}/rotate-secret`,
        method: "post",
        values: { clientSecret: newSecret },
      },
      {
        onSuccess: () => {
          setRotateOpen(false);
          // The new outbound secret is encrypted server-side (L2) and never re-fetched — drop the
          // plaintext from component state the moment it's persisted; we never echo a stored value.
          setNewSecret("");
          setRotateDone(true);
          void refetch(); // the rotation is an audited attribute change — refresh the history panel.
        },
        onError: () => setRotateError(t("admin.organizations.rotate.error.generic")),
      },
    );
  }

  const history = data?.data ?? [];
  const columns: Column<OrganizationHistoryRow>[] = [
    { header: t("admin.organizations.history.when"), cell: (h) => formatDateTime(h.occurredAt, locale) },
    { header: t("admin.organizations.history.actor"), cell: (h) => h.actor || "—" },
    { header: t("admin.organizations.history.attribute"), cell: (h) => h.attribute || "—" },
    { header: t("admin.organizations.history.old"), cell: (h) => h.oldValue || "—" },
    { header: t("admin.organizations.history.new"), cell: (h) => h.newValue || "—" },
  ];
  const bound = boundData?.data ?? [];
  const boundColumns: Column<BoundCredential>[] = [
    { header: t("admin.organizations.credentials.serviceAccount"), cell: (c) => c.serviceAccountUsername },
    { header: t("admin.sa.cred.clientId"), cell: (c) => <code>{c.clientId}</code> },
    { header: t("admin.sa.cred.scopes"), cell: (c) => c.scopes || "—" },
    { header: t("admin.sa.cred.lastUsed"), cell: (c) => formatDateTime(c.lastUsedAt, locale) },
  ];

  return (
    <>
    <Modal
      open={organization !== null}
      title={organization?.displayName || organization?.clientId || ""}
      onClose={onClose}
      footer={
        <>
          {organization ? (
            <Button
              variant="secondary"
              disabled={mutating}
              onClick={() => setConfirmAction(organization.active ? "delete" : "restore")}
            >
              {t(organization.active ? "admin.organizations.detail.softDelete" : "admin.organizations.detail.restore")}
            </Button>
          ) : null}
          <Button variant="secondary" onClick={onClose}>
            {t("admin.common.close")}
          </Button>
        </>
      }
    >
      {organization ? (
        <div className="fawkes-admin__detail">
          <dl className="fawkes-admin__fields">
            <div className="fawkes-admin__field">
              <dt>{t("admin.organizations.column.status")}</dt>
              <dd>
                <StatusBadge tone={organization.active ? "success" : "warning"}>
                  {t(organization.active ? "admin.common.active" : "admin.organizations.status.softDeleted")}
                </StatusBadge>
              </dd>
            </div>
            <div className="fawkes-admin__field">
              <dt>{t("admin.organizations.column.displayName")}</dt>
              <dd>{organization.displayName || "—"}</dd>
            </div>
            {/* The backoffice must be configured with exactly this id (fawkes.platform.organization-uuid);
                without it on screen operators copied the dev seed constant instead. */}
            <div className="fawkes-admin__field">
              <dt>{t("admin.organizations.column.id")}</dt>
              <dd>
                <code>{organization.id}</code>
                <br />
                <small>{t("admin.organizations.help.id")}</small>
              </dd>
            </div>
            <div className="fawkes-admin__field">
              <dt>{t("admin.organizations.column.clientId")}</dt>
              <dd>{organization.clientId}</dd>
            </div>
            <div className="fawkes-admin__field">
              <dt>{t("admin.organizations.column.baseUrl")}</dt>
              <dd>{organization.baseUrl}</dd>
            </div>
          </dl>

          <div className="fawkes-admin__actions">
            {/* Identity edit + test-connection + rotate-secret are only meaningful for a LIVE organization. */}
            {organization.active ? (
              <Button variant="secondary" onClick={openEdit}>
                {t("admin.organizations.detail.edit")}
              </Button>
            ) : null}
            <Button
              variant="secondary"
              onClick={runTestConnection}
              disabled={testing || !organization.active}
            >
              {testing ? t("admin.organizations.testConnection.testing") : t("admin.organizations.testConnection.button")}
            </Button>
            {organization.active ? (
              <Button variant="secondary" onClick={openRotate}>
                {t("admin.organizations.rotate.open")}
              </Button>
            ) : null}
            {organization.active ? (
              <Button variant="secondary" onClick={() => setRepairOpen(true)}>
                {t("admin.pairing.reopen")}
              </Button>
            ) : null}
          </div>
          {conn === "error" ? (
            <p className="fawkes-portal__error" role="alert">
              {t("admin.organizations.testConnection.error")}
            </p>
          ) : conn ? (
            conn.ok ? (
              <p className="fawkes-portal__banner fawkes-portal__banner--success" role="status">
                {t("admin.organizations.testConnection.ok")} — {conn.reason}
              </p>
            ) : (
              <p className="fawkes-portal__error" role="alert">
                {t("admin.organizations.testConnection.failed")} — {conn.reason}.{" "}
                {t("admin.organizations.testConnection.hint")}
              </p>
            )
          ) : null}
          {rotateDone ? (
            <p className="fawkes-portal__banner fawkes-portal__banner--success" role="status">
              {t("admin.organizations.rotate.success")}
            </p>
          ) : null}
          {actionNote ? (
            <p className="fawkes-portal__banner fawkes-portal__banner--success" role="status">
              {actionNote}
            </p>
          ) : null}

          {actionError ? (
            <p className="fawkes-portal__error" role="alert">
              {actionError}
            </p>
          ) : null}

          {repairOpen ? (
            // The code is shown once, inside this dialog. onChanged closes the whole detail (and with it this
            // dialog) before the code renders, so issuing only refreshes the history panel.
            <PairingDialog
              organization={organization}
              onClose={() => setRepairOpen(false)}
              onIssued={() => void refetch()}
            />
          ) : null}

          {organization.active ? (
            <>
              <h3 className="fawkes-admin__section">{t("admin.organizations.credentials.title")}</h3>
              <p className="fawkes-portal__muted">{t("admin.organizations.credentials.help")}</p>
              {boundError ? (
                <p className="fawkes-portal__error" role="alert">
                  {t("admin.organizations.credentials.error")}
                </p>
              ) : (
                <DataTable
                  columns={boundColumns}
                  rows={bound}
                  getRowKey={(c) => c.credentialId}
                  empty={
                    <span className="fawkes-portal__muted">{t("admin.organizations.credentials.empty")}</span>
                  }
                />
              )}
            </>
          ) : null}

          <h3 className="fawkes-admin__section">{t("admin.organizations.history.title")}</h3>
          {isLoading ? (
            <p className="fawkes-portal__muted">{t("state.loading")}</p>
          ) : isError ? (
            <p className="fawkes-portal__error" role="alert">
              {t("admin.organizations.history.error")}
            </p>
          ) : (
            <DataTable
              columns={columns}
              rows={history}
              getRowKey={(h) =>
                `${h.occurredAt ?? "?"}:${h.attribute ?? "?"}:${h.oldValue ?? "?"}:${h.newValue ?? "?"}`
              }
              empty={<span className="fawkes-portal__muted">{t("admin.organizations.history.empty")}</span>}
            />
          )}
        </div>
      ) : null}
    </Modal>

    {/* Single soft-delete confirm (DANGER) / restore confirm */}
    <Modal
      open={confirmAction === "delete"}
      title={t("admin.organizations.detail.confirm.deleteTitle")}
      onClose={() => setConfirmAction(null)}
      footer={
        <>
          <Button variant="secondary" onClick={() => setConfirmAction(null)}>
            {t("admin.common.cancel")}
          </Button>
          <Button variant="secondary" disabled={mutating} onClick={runSoftDeleteOrRestore}>
            {t("admin.organizations.detail.confirm.deleteYes")}
          </Button>
        </>
      }
    >
      <p className="fawkes-portal__muted">{t("admin.organizations.detail.confirm.deleteBody")}</p>
    </Modal>
    <Modal
      open={confirmAction === "restore"}
      title={t("admin.organizations.detail.confirm.restoreTitle")}
      onClose={() => setConfirmAction(null)}
      footer={
        <>
          <Button variant="secondary" onClick={() => setConfirmAction(null)}>
            {t("admin.common.cancel")}
          </Button>
          <Button disabled={mutating} onClick={runSoftDeleteOrRestore}>
            {t("admin.organizations.detail.confirm.restoreYes")}
          </Button>
        </>
      }
    >
      <p className="fawkes-portal__muted">{t("admin.organizations.detail.confirm.restoreBody")}</p>
    </Modal>

    {/* Edit identity — displayName/baseUrl/clientId → PUT. Never touches the encrypted secret (L2). */}
    <Modal
      open={editOpen}
      title={t("admin.organizations.detail.edit")}
      onClose={() => setEditOpen(false)}
      footer={
        <>
          <Button variant="secondary" onClick={() => setEditOpen(false)}>
            {t("admin.common.cancel")}
          </Button>
          <Button onClick={submitEdit} disabled={savingEdit}>
            {savingEdit ? t("admin.common.saving") : t("admin.organizations.detail.save")}
          </Button>
        </>
      }
    >
      <Field
        label={t("admin.organizations.column.displayName")}
        value={editForm.displayName}
        onChange={(e) => setEditForm((f) => ({ ...f, displayName: e.target.value }))}
      />
      <p className="fawkes-portal__muted">{t("admin.organizations.help.displayName")}</p>
      <Field
        label={t("admin.organizations.column.baseUrl")}
        value={editForm.baseUrl}
        onChange={(e) => setEditForm((f) => ({ ...f, baseUrl: e.target.value }))}
      />
      <p className="fawkes-portal__muted">{t("admin.organizations.help.baseUrl")}</p>
      <Field
        label={t("admin.organizations.column.clientId")}
        value={editForm.clientId}
        onChange={(e) => setEditForm((f) => ({ ...f, clientId: e.target.value }))}
      />
      <p className="fawkes-portal__muted">{t("admin.organizations.help.clientId")}</p>
      {editError ? (
        <p className="fawkes-portal__error" role="alert">
          {editError}
        </p>
      ) : null}
    </Modal>

    {/* Rotate client secret — show-once new plaintext; the stored value is never echoed (the DTO omits
        it). The save re-encrypts (L2), evicts the organization's token cache, and audits the change. */}
    <Modal
      open={rotateOpen}
      title={t("admin.organizations.rotate.title")}
      onClose={() => setRotateOpen(false)}
      footer={
        <>
          <Button variant="secondary" onClick={() => setRotateOpen(false)}>
            {t("admin.common.cancel")}
          </Button>
          <Button onClick={submitRotate} disabled={rotating || !newSecret}>
            {rotating ? t("admin.common.saving") : t("admin.organizations.rotate.submit")}
          </Button>
        </>
      }
    >
      <p className="fawkes-portal__muted">{t("admin.organizations.rotate.hint")}</p>
      <PasswordField
        label={t("admin.organizations.rotate.newSecret")}
        revealLabel={t("auth.field.revealPassword")}
        hideLabel={t("auth.field.hidePassword")}
        value={newSecret}
        onChange={(e) => setNewSecret(e.target.value)}
      />
      {rotateError ? (
        <p className="fawkes-portal__error" role="alert">
          {rotateError}
        </p>
      ) : null}
    </Modal>
    </>
  );
}
